import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { auth, EmailAuthProvider, reauthenticateWithCredential, updateEmail, updatePassword } from '../firebase';

interface SecurityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SecurityModal: React.FC<SecurityModalProps> = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState<'email' | 'password' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form states
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const resetForm = () => {
    setEmail('');
    setCurrentPassword('');
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser || !auth.currentUser.email) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updateEmail(auth.currentUser, email);
      setSuccess('Email updated successfully.');
      setTimeout(() => {
        setMode(null);
        resetForm();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update email.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser || !auth.currentUser.email) return;

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, oldPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      setSuccess('Password updated successfully.');
      setTimeout(() => {
        setMode(null);
        resetForm();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="security-modal-wrapper" className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!loading) {
                setMode(null);
                resetForm();
                onClose();
              }
            }}
            className="absolute inset-0 bg-black/75"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-md bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col text-white"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/[0.06] bg-[#322D29] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                {mode ? (
                  <button 
                    onClick={() => {
                      setMode(null);
                      resetForm();
                    }}
                    className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer text-white"
                  >
                    <i className="fas fa-arrow-left text-xs"></i>
                  </button>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-[#F26522] flex items-center justify-center text-white">
                    <i className="fas fa-shield-alt text-sm"></i>
                  </div>
                )}
                <div>
                  <h2 className="text-sm font-bold uppercase tracking-widest text-white font-heading">
                    {mode === 'email' ? 'Change Email' : mode === 'password' ? 'Change Password' : 'Sign in & Security'}
                  </h2>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/40 font-heading mt-0.5">
                    Account Protection
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setMode(null);
                  resetForm();
                  onClose();
                }}
                className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex-1 overflow-y-auto custom-scrollbar bg-[#3a3430]">
              {error && (
                <div className="mb-4 p-3.5 bg-red-950/30 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wider rounded-lg">
                  {error}
                </div>
              )}
              {success && (
                <div className="mb-4 p-3.5 bg-emerald-950/30 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider rounded-lg">
                  {success}
                </div>
              )}

              {!mode ? (
                <div className="space-y-3">
                  <button
                    onClick={() => setMode('email')}
                    className="w-full flex items-center justify-between p-4 border border-white/[0.06] bg-[#322D29] hover:bg-white/5 rounded-lg transition-all group cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white/60 group-hover:text-[#F26522] transition-colors">
                        <i className="fas fa-envelope text-xs"></i>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest font-heading text-white block">Email Address</span>
                        <span className="text-[9px] text-white/40 font-medium">{auth.currentUser?.email}</span>
                      </div>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                  </button>

                  <button
                    onClick={() => setMode('password')}
                    className="w-full flex items-center justify-between p-4 border border-white/[0.06] bg-[#322D29] hover:bg-white/5 rounded-lg transition-all group cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white/60 group-hover:text-[#F26522] transition-colors">
                        <i className="fas fa-key text-xs"></i>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest font-heading text-white block">Password</span>
                        <span className="text-[9px] text-white/40 font-medium">Last changed recently</span>
                      </div>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                  </button>
                </div>
              ) : mode === 'email' ? (
                <form onSubmit={handleEmailChange} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">New Email Address</label>
                    <input
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-[#322D29] border border-white/[0.08] rounded-lg px-3 py-2.5 text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-all"
                      placeholder="Enter new email..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Current Password</label>
                    <input
                      required
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full bg-[#322D29] border border-white/[0.08] rounded-lg px-3 py-2.5 text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-all"
                      placeholder="Confirm identity..."
                    />
                  </div>
                  <button
                    disabled={loading}
                    type="submit"
                    className="w-full bg-[#F26522] text-white py-3.5 font-bold uppercase text-[10px] tracking-[0.2em] rounded-lg hover:bg-[#F26522]/90 transition-all disabled:opacity-50 font-heading cursor-pointer"
                  >
                    {loading ? 'Processing...' : 'Update Email'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Old Password</label>
                    <input
                      required
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="w-full bg-[#322D29] border border-white/[0.08] rounded-lg px-3 py-2.5 text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-all"
                      placeholder="Current password..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">New Password</label>
                    <input
                      required
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full bg-[#322D29] border border-white/[0.08] rounded-lg px-3 py-2.5 text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-all"
                      placeholder="New password..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Confirm New Password</label>
                    <input
                      required
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-[#322D29] border border-white/[0.08] rounded-lg px-3 py-2.5 text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-all"
                      placeholder="Confirm new password..."
                    />
                  </div>
                  <button
                    disabled={loading}
                    type="submit"
                    className="w-full bg-[#F26522] text-white py-3.5 font-bold uppercase text-[10px] tracking-[0.2em] rounded-lg hover:bg-[#F26522]/90 transition-all disabled:opacity-50 font-heading cursor-pointer"
                  >
                    {loading ? 'Processing...' : 'Update Password'}
                  </button>
                </form>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-[#322D29] border-t border-white/[0.06] flex flex-col items-center gap-1">
              <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-white/40 font-heading">
                SECURE PROTOCOL ACTIVE
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SecurityModal;
